import { Theme, css } from '@emotion/react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { IChatCharacter, IChatSituationDetail } from '@model';

const containerCSS = () => css`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.large}px;
  margin-top: ${Spacing.large}px;
`;

const listCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  padding: 0 ${Spacing.medium}px;

  .title {
    ${Typography.h5};
    padding: 10px 0;
    margin-bottom: 10px;
  }
  .chipContainer {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .chip {
    padding: 10px ${Spacing.small}px;
    border-radius: ${Radius.medium}px;
    background-color: ${theme.var.gray50};
    color: ${theme.var.gray900};
    ${Typography.body14_sentence};
    line-height: 150%;
  }
  .fill {
    width: 100%;
    padding: ${Spacing.medium}px;
  }
`;

interface CharacterDetailInfoProps {
  character: IChatCharacter;
  situationInfo?: IChatSituationDetail;
  isMyCharacter?: boolean;
}

const CharacterDetailInfo = ({
  character,
  situationInfo,
  isMyCharacter = false,
}: CharacterDetailInfoProps) => {
  const { information } = character;

  const list = [
    {
      id: 'profile',
      title: '🔍 프로필',
      value: [information.gender, information.age + '세', information.job],
    },
    {
      id: 'tag',
      title: '⚡ 태그',
      value: character.tags.map((v) => '#' + v.name) || [],
    },
    {
      id: 'personality',
      title: '🌟 성격',
      value: [information?.personality] || [],
      type: 'fill',
    },
    ...(information?.appearance
      ? [
          {
            id: 'appearance',
            title: '👀 외모',
            value: [information?.appearance] || [],
            type: 'fill',
          },
        ]
      : []),

    ...(information?.backstory
      ? [
          {
            id: 'backstory',
            title: '🌎 세계관',
            value: [information?.backstory] || [],
            type: 'fill',
          },
        ]
      : []),
    ...(isMyCharacter
      ? [
          {
            id: 'hidden_characteristics',
            title: '🔒 나만 아는 캐릭터 비밀',
            value: [information?.hidden_characteristics] || [],
            type: 'fill',
          },
        ]
      : []),
    {
      id: 'genre',
      title: '장르 및 소재',
      value: situationInfo?.tag_set?.map((v) => '#' + v.name) || [],
    },
    {
      id: 'theme',
      title: '그들의 관계',
      value: [situationInfo?.chemi_story?.title] || [],
    },
  ];

  const filterGender = (value?: string) => {
    if (!value) {
      return;
    }

    switch (value) {
      case 'male':
      case '남자':
        return '남성';
      case 'female':
      case '여자':
        return '여성';
      case 'unknown':
        return '알 수 없음';
      default:
        return value;
    }
  };

  return (
    <div css={containerCSS}>
      {list.map((item, idx) =>
        item.value.length === 1 && item.value[0] === '' ? null : (
          <div css={listCSS} key={`list_${idx}`}>
            <p className="title">{item.title}</p>
            <div className="chipContainer">
              {item.value.map(
                (val, idx) =>
                  val !== '' && (
                    <div
                      key={`${item.id}_${idx}`}
                      className={`chip ${item.type}`}
                    >
                      <p className="multiline150">{filterGender(val)}</p>
                    </div>
                  )
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CharacterDetailInfo;
